import React, { useRef, useState } from 'react';
import { EmailPreview } from './EmailPreview';
import { DateTime } from 'luxon';
import { getIconComponent } from '../../../../../../util/reactFunc';
import { repository } from '@whizz/api-client';
import { Translate, useI18n } from '@whizz/react-i18n';
import { Formik } from 'formik';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { SaveButton } from '../../../../../../global';
import { Button, Input, Loader } from '../../../../../../components';
import { generateDateTime, getMondaysSinceDate } from '../../../../../../util/dates';
import { debounce } from 'lodash';
import GenericSponsorImage from '../../../../../../assets/generic-sponsorship.png';

const MAX_FILE_SIZE = 100000;

const Sponsorship = ({ totalParents }: { totalParents: number }): JSX.Element => {
  const { translate } = useI18n();
  const { data: sponsorships, isLoading } = useQuery(
    'sponsorships',
    () => repository.SponsorshipUploadRepository.getSponsorship(),
  );
  const queryClient = useQueryClient();

  const onSuccess = () => queryClient.invalidateQueries('sponsorships');

  const loading = isLoading;

  if (loading) {
    return <Loader />;
  }

  const modfiedDateTime = sponsorships?.modified ? generateDateTime(sponsorships.modified) : null;
  const nextWeek = DateTime.now().plus({ weeks: 1 });
  const hasSponsorship = null != sponsorships?.image;

  return (
    <div className={'grid grid-cols-2 gap-10'}>
      <div className={'space-y-4'}>
        {hasSponsorship ? (
          <>
            <p>{translate('pages.ROSTER_MANAGEMENT.SPONSORSHIP.WITH_SPONSOR.p1')}</p>
            {modfiedDateTime ? (
              <p><Translate i18nKey={'pages.ROSTER_MANAGEMENT.SPONSORSHIP.WITH_SPONSOR.p2'} values={{ added: modfiedDateTime.toFormat('d MMM yyyy') }}>
                { /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                /* @ts-ignore */ }
                <strong />
              </Translate></p>
            ) : null}
            {modfiedDateTime ? (<p><Translate i18nKey={'pages.ROSTER_MANAGEMENT.SPONSORSHIP.WITH_SPONSOR.p3'} values={{ count: getMondaysSinceDate(modfiedDateTime) }}>
              { /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
              /* @ts-ignore */ }
              <strong />
            </Translate></p>
            ) : null}
            {totalParents ? (
              <p><Translate i18nKey={'pages.ROSTER_MANAGEMENT.SPONSORSHIP.WITH_SPONSOR.p4'} values={{ parents: totalParents }}>
                { /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                /* @ts-ignore */ }
                <strong />
              </Translate></p>
            ) : null}
          </>
        )
          : (
            <>
              <p>{translate('pages.ROSTER_MANAGEMENT.SPONSORSHIP.NO_SPONSOR.p1')}</p>
              <p>{translate('pages.ROSTER_MANAGEMENT.SPONSORSHIP.NO_SPONSOR.p2')}</p>
              <p>{translate('pages.ROSTER_MANAGEMENT.SPONSORSHIP.NO_SPONSOR.p3')}</p>
              <p>{translate('pages.ROSTER_MANAGEMENT.SPONSORSHIP.NO_SPONSOR.p4')}</p>
            </>
          )}
        <FileUploadForm onSuccess={onSuccess} onError={() => {console.log('error');}} formData={sponsorships ?? null}/>
        <hr />
        <DeactivateSponsorship hasSponsorship={hasSponsorship} onSuccess={onSuccess} />
      </div>
      <EmailPreview previewData={
        {
          isEnabled: true,
          fullName: translate('pages.ROSTER_MANAGEMENT.SPONSORSHIP.PREVIEW.FULL_NAME'),
          firstName: translate('pages.ROSTER_MANAGEMENT.SPONSORSHIP.PREVIEW.FIRST_NAME'),
          startDate: nextWeek.startOf('week'),
          endDate: nextWeek.endOf('week'),
          sponsorshipImage: sponsorships?.image ?? GenericSponsorImage,
          sponsorshipLink: sponsorships?.link ?? null,
        }
      }/>
    </div>
  );
};

type SponsorshipFormData = {
  image: string;
  link: string | null;
} | null;
interface FileUploadForm {
  onSuccess: () => void;
  onError: () => void;
  formData: SponsorshipFormData;
}

const normalizeUrl = (url: string): string => {
  if (!url) {
    return '';
  }
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
};

let currentController: AbortController | null = null;

const checkUrl = debounce(async (url: string, setUrlError: (errorType: string) => void) => {
  if (!url.trim()) {
    return;
  }
  if (currentController) {
    currentController.abort();
  }
  currentController = new AbortController();
  try {
    url = normalizeUrl(url);
    new URL(url);
    await fetch(url, { method: 'HEAD', mode: 'no-cors', signal: currentController.signal });
    setUrlError('valid');
  } catch (error) {
    setUrlError('invalid');
  }
}, 750);

const FileUploadForm = ({ onSuccess, onError, formData }: FileUploadForm): JSX.Element => {
  const { translate } = useI18n();

  const inputFileRef = useRef<HTMLInputElement>(null);
  const [ urlError, setUrlError ] = useState<'valid' | 'invalid' | 'checking'>('valid');

  const uploadMutation = useMutation(({ files, link }: { files: FileList, link: string }) => {
    return repository.SponsorshipUploadRepository.addFiles(files, typeof link === 'string' ? normalizeUrl(link) : undefined);
  });

  return (
    <Formik<{ files: FileList | null, link: string }>
      onSubmit={(values, { resetForm }) => {
        if (!values.files || values.files.length === 0) {
          return;
        }
        uploadMutation.mutate(
          { files: values.files, link: values.link },
          {
            onSuccess: () => {
              onSuccess();
              resetForm();
              if (inputFileRef.current) {
                inputFileRef.current.value = '';
              }
            },
            onError: () => {
              onError();
            },
          },
        );
      }}
      initialValues={{ files: null, link: formData?.link ?? '' }}
      enableReinitialize
    >
      {({ values, getFieldProps, setFieldValue, handleSubmit }) => {
        const fileSizeTooBig = null != values.files && 0 < values.files.length && values.files[0].size > MAX_FILE_SIZE;
        const invalid = null == values.files || 0 === values.files.length || fileSizeTooBig || (values.link.length > 0 && urlError !== 'valid');
        return (
          <form onSubmit={handleSubmit} className={'space-y-4'}>
            <div className={'flex items-center space-x-2'}>
              <p>{translate(`pages.ROSTER_MANAGEMENT.SPONSORSHIP.FORM.LABELS.${ formData?.image ? 'CHANGE_SPONSOR' : 'SPONSOR_IMAGE'}`)}</p>
              <SaveButton onClick={() => inputFileRef?.current?.click()}>
                {translate('pages.ROSTER_MANAGEMENT.SPONSORSHIP.BUTTONS.SELECT')}
              </SaveButton>
              <input
                ref={inputFileRef}
                type={'file'}
                accept={'.jpg,.jpeg,.png'}
                className={'hidden'}
                onChange={(event) => setFieldValue('files', event.target.files)}
              />
              {fileSizeTooBig ? <h4 className={'text-error font-bold'}>{translate('pages.ROSTER_MANAGEMENT.SPONSORSHIP.FORM.ERRORS.FILE_SIZE_TOO_BIG')}</h4> : (
                (null != values.files && 0 < values.files.length) && (
                  <div className={'flex items-center'}>
                    {getIconComponent('Attachment')}<span className={'ml-2'}>{values.files[0].name}</span>
                  </div>
                )
              )}
            </div>
            <p>{translate('pages.ROSTER_MANAGEMENT.SPONSORSHIP.FORM.LABELS.SPONSOR_IMAGE_HINT')}</p>
            <div className={'flex flex-col space-y-1'}>
              <Input
                {...getFieldProps('link')}
                id={'sponsorship-link'}
                label={translate('pages.ROSTER_MANAGEMENT.SPONSORSHIP.FORM.LABELS.SPONSOR_URL')}
                placeholder={translate('pages.ROSTER_MANAGEMENT.SPONSORSHIP.FORM.LABELS.SPONSOR_URL_HINT')}
                type={'text'}
                isInline
                onChange={(event) => {
                  setUrlError('checking');
                  setFieldValue('link', event.target.value);
                  checkUrl(event.target.value, setUrlError);
                }}
              />
              {urlError === 'invalid' ? <h4 className={'text-error font-bold text-sm self-end'}>{translate('pages.ROSTER_MANAGEMENT.SPONSORSHIP.FORM.ERRORS.INVALID_URL')}</h4> : undefined}
            </div>
            <Button
              type={'submit'}
              icon={'Publish'}
              color={'positive'}
              isOutline
              disabled={invalid || uploadMutation.isLoading}
              className={'w-full'}
            >
              {translate('pages.ROSTER_MANAGEMENT.SPONSORSHIP.BUTTONS.UPLOAD')}
            </Button>
          </form>
        );}
      }
    </Formik>
  );
};

const DeactivateSponsorship = ({ hasSponsorship, onSuccess }: { hasSponsorship: boolean, onSuccess: () => void; }) => {
  const { translate } = useI18n();
  const deactivateMutation = useMutation(() => repository.SponsorshipUploadRepository.deactivate());
  return (
    <Button
      onClick={() => deactivateMutation.mutate(undefined, { onSuccess })}
      color={'negative'}
      isOutline
      disabled={!hasSponsorship || deactivateMutation.isLoading}
      className={'w-full'}
    >
      {translate('pages.ROSTER_MANAGEMENT.SPONSORSHIP.BUTTONS.DEACTIVATE')}
    </Button>
  );
};

export default Sponsorship;
